// Section 22 Report overlay at season start: deaths by name, age and cause with their three
// contributing factors, births, illness and recoveries, crimes and justice, events with links to
// their prompts, production versus last season, shortages with who went short, warnings, the
// steady-state result at year end, and the outcome. Narration comes from api/narrate.ts; the
// strings are in ui/text/en.ts.
import {
  catalog,
  narrate,
  steadyTarget,
  type DeathCause,
  type Factor,
  type GameState,
  type Narration,
  type NeedId,
  type PersonId,
  type Prompt,
  type PromptId,
  type RationResource,
  type ReportEntry,
  type Resource,
  type Season,
  type SeasonReport,
  type SteadyCondition,
} from '../sim/api';
import { memo } from './memo';

export interface DeathLine {
  personId: PersonId;
  name: string;
  ageYears: number;
  cause: DeathCause;
  illnessId: string | null;
  jobLabel: string;
  /** Up to three contributing factors (Section 22). */
  factors: Factor[];
}

export interface BirthLine {
  personId: PersonId;
  name: string;
  mother: string;
  father: string | null;
}

export interface ShortageLine {
  resource: RationResource;
  covered: number;
  population: number;
  shortfall: number;
  /** Everyone below the short threshold for this need, lowest share first. */
  whoShort: { personId: PersonId; name: string; share: number }[];
}

export interface ProductionLine {
  resource: Resource;
  now: number;
  /** Last season's production, or null if this is the first report. */
  before: number | null;
}

export interface PromptLink {
  id: PromptId;
  kind: Prompt['kind'];
  /** The event id for event prompts; the case or pairing otherwise. */
  subject: string;
  answered: boolean;
}

export interface ReportVM {
  turn: number;
  year: number;
  season: Season;
  population: SeasonReport['population'];
  deaths: DeathLine[];
  births: BirthLine[];
  health: Narration[];
  crime: Narration[];
  events: Narration[];
  prompts: PromptLink[];
  production: ProductionLine[];
  producers: Narration[];
  shortages: ShortageLine[];
  warnings: Narration[];
  steady: { pass: boolean; counter: number; target: number; failed: SteadyCondition[] } | null;
  outcome: GameState['outcome'];
  /** Everything else that was narrated (construction, coming of age, policy changes, ...). */
  other: Narration[];
  /** Kept for callers of the M1 overlay. */
  lines: Narration[];
}

const HEALTH = new Set<ReportEntry['kind']>(['illness', 'recovery', 'injury', 'treatment', 'miscarriage']);
const CRIME = new Set<ReportEntry['kind']>(['crime', 'justice']);
const EVENTS = new Set<ReportEntry['kind']>(['event', 'event-choice', 'foreshadow', 'arrival', 'departure']);
const HANDLED = new Set<ReportEntry['kind']>([
  'death',
  'birth',
  'warning',
  'production',
  'shortage',
  'steady',
  'outcome',
  'spoilage',
]);
const NEED_OF: Record<RationResource, NeedId> = {
  food: 'food',
  water: 'water',
  warmth: 'warmth',
  medicine: 'healthCare',
};
const PRODUCED: Resource[] = ['food', 'water', 'wood', 'stone', 'tools', 'medicine', 'cloth'];

/** A death entry as a report line (also used by the History archive). */
export function deathLine(e: Extract<ReportEntry, { kind: 'death' }>): DeathLine {
  return {
    personId: e.personId,
    name: e.name,
    ageYears: Math.floor(e.ageSeasons / catalog().seasonsPerYear),
    cause: e.cause,
    illnessId: e.illnessId ?? null,
    jobLabel: catalog().jobs.find((j) => j.id === e.jobId)?.label ?? e.jobId,
    factors: e.factors.slice(0, 3),
  };
}

function nameOf(game: GameState, id: PersonId): string {
  return game.people[id]?.name ?? game.formerPeople[id]?.name ?? id;
}

function shortageLine(
  game: GameState,
  report: SeasonReport,
  e: Extract<ReportEntry, { kind: 'shortage' }>,
): ShortageLine {
  const threshold = catalog().shortThreshold;
  const need = NEED_OF[e.resource];
  const whoShort = (Object.entries(report.needs) as [PersonId, Record<NeedId, number>][])
    .filter(([, n]) => n[need] < threshold)
    .map(([id, n]) => ({ personId: id, name: nameOf(game, id), share: n[need] }))
    .sort((a, b) => a.share - b.share || a.name.localeCompare(b.name));
  return {
    resource: e.resource,
    covered: e.covered,
    population: e.population,
    shortfall: e.shortfall,
    whoShort,
  };
}

function subjectOf(p: Prompt): string {
  switch (p.kind) {
    case 'event':
      return p.eventId;
    case 'justice':
      return p.crime;
    case 'pairing':
      return p.personIds.join(' ');
    case 'shortage':
      return p.resource;
  }
}

export const reportVM = memo((game: GameState, answered?: Readonly<Record<PromptId, unknown>>): ReportVM | null => {
  const report = game.history[game.history.length - 1];
  if (report === undefined) return null;
  const previous = game.history[game.history.length - 2];
  const vm: ReportVM = {
    turn: report.turn,
    year: report.year,
    season: report.season,
    population: report.population,
    deaths: [],
    births: [],
    health: [],
    crime: [],
    events: [],
    prompts: game.openPrompts.map((p) => ({
      id: p.id,
      kind: p.kind,
      subject: subjectOf(p),
      answered: answered !== undefined && p.id in answered,
    })),
    production: PRODUCED.map((r) => ({
      resource: r,
      now: report.flows[r].production,
      before: previous === undefined ? null : previous.flows[r].production,
    })).filter((l) => l.now > 0 || (l.before ?? 0) > 0),
    producers: [],
    shortages: [],
    warnings: [],
    steady: null,
    outcome: game.outcome,
    other: [],
    lines: [],
  };
  for (const e of report.entries) {
    const n = narrate(e, game);
    if (n !== null) vm.lines.push(n);
    switch (e.kind) {
      case 'death':
        vm.deaths.push(deathLine(e));
        break;
      case 'birth':
        vm.births.push({
          personId: e.personId,
          name: e.name,
          mother: nameOf(game, e.motherId),
          father: e.fatherId === null ? null : nameOf(game, e.fatherId),
        });
        break;
      case 'warning':
        if (n !== null) vm.warnings.push(n);
        break;
      case 'production':
      case 'spoilage':
        if (n !== null) vm.producers.push(n);
        break;
      case 'shortage':
        vm.shortages.push(shortageLine(game, report, e));
        break;
      case 'steady': {
        const failed = (Object.entries(e.result.conditions) as [SteadyCondition, { pass: boolean }][])
          .filter(([, c]) => !c.pass)
          .map(([id]) => id);
        vm.steady = {
          pass: e.result.allPass,
          counter: e.result.counter,
          // Section 20 sandbox: the longer target once the run has been won.
          target: game.sandbox ? steadyTarget(game) : e.result.target,
          failed,
        };
        break;
      }
      default:
        if (n === null || HANDLED.has(e.kind)) break;
        if (HEALTH.has(e.kind)) vm.health.push(n);
        else if (CRIME.has(e.kind)) vm.crime.push(n);
        else if (EVENTS.has(e.kind)) vm.events.push(n);
        else vm.other.push(n);
    }
  }
  return vm;
});
